/**
 * Position Detail Page
 * 
 * Displays full details for a single sex position including:
 * - Name, category, difficulty badges
 * - Key stats (difficulty, stamina, flexibility, intimacy)
 * - Description and page sections
 * - Compatible kinks, actions, gear
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft, Activity, Zap, Heart, Eye, Users, Shield,
    ChevronRight, ChevronDown, Sparkles, Layers, BookOpen,
    Target, TrendingUp
} from 'lucide-react';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Category colors
const getCategoryColor = (category) => {
    const colors = {
        'missionary': 'text-rose-400 bg-rose-500/10 border-rose-500/20',
        'doggy': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        'cowgirl': 'text-pink-400 bg-pink-500/10 border-pink-500/20',
        'standing': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
        'sitting': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
        'side': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        'oral': 'text-wine-400 bg-wine-500/10 border-wine-500/20'
    };
    return colors[category?.toLowerCase()] || 'text-gray-400 bg-gray-500/10 border-gray-500/20';
};

// Section configuration
const SECTION_CONFIG = {
    how_to: { icon: Zap, title: 'How to Get Into It', color: 'text-amber-400' },
    sensation_profile: { icon: Heart, title: 'Sensation Profile', color: 'text-pink-400' },
    variations: { icon: Layers, title: 'Variations', color: 'text-purple-400' },
    transitions: { icon: Activity, title: 'Transitions', color: 'text-blue-400' },
    stamina_requirements: { icon: Target, title: 'Stamina Needs', color: 'text-orange-400' },
    eye_contact: { icon: Eye, title: 'Eye Contact Options', color: 'text-rose-400' },
    combinations: { icon: Sparkles, title: 'Pairs Well With', color: 'text-purple-400' },
    progression: { icon: ChevronRight, title: "What's Next", color: 'text-indigo-400' },
    physical_requirements: { icon: Activity, title: 'Physical Requirements', color: 'text-orange-400' }
};

// Stat bar component
const StatBar = ({ label, value, max = 10, color = 'wine', icon: Icon }) => {
    const percentage = Math.min(100, (value / max) * 100);
    return (
        <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-cream/70 flex items-center gap-2">
                    {Icon && <Icon size={14} className="text-cream/50" />}
                    {label}
                </span>
                <span className="text-sm font-medium text-cream/90">{value}/{max}</span>
            </div>
            <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full bg-${color}-500 transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};

// Page section component with accordion
const PageSection = ({ sectionKey, content, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const config = SECTION_CONFIG[sectionKey] || { icon: BookOpen, title: sectionKey, color: 'text-gray-400' };
    const Icon = config.icon;

    if (!content) return null;

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300 mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none"
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{config.title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="prose prose-invert max-w-none text-cream/70 leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Related entity link component
const EntityLink = ({ to, name, category }) => (
    <Link
        to={to}
        className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
    >
        <div>
            <span className="text-white group-hover:text-wine-300 transition-colors">{name}</span>
            {category && <span className="ml-2 text-sm text-dark-500">{category}</span>}
        </div>
        <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
    </Link>
);

const PositionDetailPage = () => {
    const { id } = useParams();
    const [position, setPosition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPosition();
    }, [id]);

    const fetchPosition = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getPosition(id);
            setPosition(data);
        } catch (err) {
            console.error('Error fetching position:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading position...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !position) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">🛏️</div>
                    <h2 className="text-xl font-bold text-white mb-2">Position Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This position does not exist.'}</p>
                    <Link to="/browse?tab=positions" className="btn-primary">
                        Back to Positions
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = position.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    const categoryClass = getCategoryColor(position.category);

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/browse?tab=positions"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Positions</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {position.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/position/${position.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {position.parentVariant.name}
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-wine-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-wine-700/20 flex items-center justify-center">
                                <Activity size={28} className="text-wine-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {position.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    {position.category && (
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryClass}`}>
                                            {position.category}
                                        </span>
                                    )}
                                    {position.difficulty && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-amber-500/10 text-amber-400">
                                            <Zap size={12} className="inline mr-1" />
                                            {position.difficulty}
                                        </span>
                                    )}
                                    {position.participants && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-blue-500/10 text-blue-400">
                                            <Users size={12} className="inline mr-1" />
                                            {position.participants} participants
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {position.description && (
                            <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed mt-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{position.description}</ReactMarkdown>
                            </div>
                        )}

                        {/* Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Zap size={18} className="mx-auto mb-1 text-amber-400" />
                                <div className="text-lg font-bold text-cream">{position.difficulty_level || 5}</div>
                                <div className="text-xs text-dark-400">Difficulty</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Activity size={18} className="mx-auto mb-1 text-emerald-400" />
                                <div className="text-lg font-bold text-cream">{position.stamina_required || 5}</div>
                                <div className="text-xs text-dark-400">Stamina</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Target size={18} className="mx-auto mb-1 text-purple-400" />
                                <div className="text-lg font-bold text-cream">{position.flexibility_required || 5}</div>
                                <div className="text-xs text-dark-400">Flexibility</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Heart size={18} className="mx-auto mb-1 text-pink-400" />
                                <div className="text-lg font-bold text-cream">{position.intimacy_level || 5}</div>
                                <div className="text-xs text-dark-400">Intimacy</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Sections */}
                <div className="space-y-4">
                    {Object.keys(SECTION_CONFIG).map(key => (
                        <PageSection
                            key={key}
                            sectionKey={key}
                            content={getSectionContent(key)}
                            defaultOpen={key === 'how_to'}
                        />
                    ))}
                </div>

                {/* Related Entities */}
                {position.relatedKinks?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-wine-400" />
                            Related Kinks
                        </h3>
                        <div className="grid gap-2">
                            {position.relatedKinks.map(kink => (
                                <EntityLink
                                    key={kink.id}
                                    to={`/kink/${kink.id}`}
                                    name={kink.name}
                                    category={kink.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {position.relatedActions?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Zap className="w-5 h-5 text-amber-400" />
                            Compatible Actions
                        </h3>
                        <div className="grid gap-2">
                            {position.relatedActions.map(action => (
                                <EntityLink
                                    key={action.id}
                                    to={`/action/${action.id}`}
                                    name={action.name}
                                    category={action.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Sub-Variants */}
                {position.childVariants && position.childVariants.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-purple-400" />
                            Variants of {position.name}
                        </h3>
                        <div className="grid gap-2">
                            {position.childVariants.map(variant => (
                                <EntityLink
                                    key={variant.id}
                                    to={`/position/${variant.id}`}
                                    name={variant.name}
                                    category={variant.category}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default PositionDetailPage;
